import { motion } from 'framer-motion';
import { 
  UserCircle2, 
  Target, 
  Rocket, 
  Trophy,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const STEPS = [
  {
    icon: <UserCircle2 className="h-6 w-6" />,
    title: "Create Your Profile",
    description: "Tell us about your career goals and current situation",
    gradient: "from-blue-400 to-cyan-500",
    glow: "blue"
  },
  {
    icon: <Target className="h-6 w-6" />,
    title: "Set Your Goals",
    description: "Define your career objectives and timeline",
    gradient: "from-purple-400 to-pink-500",
    glow: "purple"
  },
  {
    icon: <Rocket className="h-6 w-6" />,
    title: "Follow Your Path",
    description: "Get personalized recommendations and track your progress",
    gradient: "from-amber-400 to-orange-500",
    glow: "orange"
  },
  {
    icon: <Trophy className="h-6 w-6" />,
    title: "Achieve Success",
    description: "Reach your career goals and celebrate your progress",
    gradient: "from-green-400 to-emerald-500",
    glow: "green"
  }
] as const;

export function HowItWorks() {
  return (
    <section id="how-it-works" className="relative py-20 overflow-hidden">
      {/* Gradient Background with Animation */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse",
            ease: "linear",
          }}
          style={{
            backgroundImage: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
            backgroundSize: '100% 100%',
          }}
        />
      </div>

      <div className="relative container mx-auto px-4">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            className="inline-flex items-center justify-center space-x-2 mb-4"
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <Sparkles className="h-6 w-6 text-primary-400" />
            <span className="text-primary-400 font-medium">Simple Process</span>
            <Sparkles className="h-6 w-6 text-primary-400" />
          </motion.div>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            How It{' '}
            <span className="bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
              Works
            </span>
          </motion.h2>
          <motion.p 
            className="text-gray-400 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Start your career journey in four simple steps
          </motion.p>
        </motion.div>

        {/* Steps */}
        <div className="max-w-5xl mx-auto">
          <div className="grid gap-8">
            {STEPS.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative"
              >
                <div className={`relative flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} 
                                items-center gap-8 p-6 rounded-xl bg-white/5 backdrop-blur-sm 
                                border border-white/10 group hover:border-white/20 
                                transition-all duration-300`}>
                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-8 h-8 rounded-full bg-slate-800 
                                border border-white/20 flex items-center justify-center text-sm 
                                font-bold text-primary-400">
                    {index + 1}
                  </div>

                  {/* Icon Container with Glow */}
                  <div className="relative flex-shrink-0">
                    <div className={`w-16 h-16 rounded-lg bg-gradient-to-br ${step.gradient} 
                                   p-4 flex items-center justify-center text-white
                                   group-hover:scale-110 transition-transform duration-300`}>
                      {step.icon}
                      <motion.div
                        className="absolute inset-0 -z-10 rounded-lg opacity-0 blur-xl
                                 group-hover:opacity-70 transition-opacity duration-500"
                        animate={{
                          boxShadow: [
                            `0 0 20px 5px var(--tw-colors-${step.glow}-500)`,
                            `0 0 30px 8px var(--tw-colors-${step.glow}-500)`,
                            `0 0 20px 5px var(--tw-colors-${step.glow}-500)`
                          ]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <h3 className={`text-xl font-semibold mb-2 bg-gradient-to-r ${step.gradient} 
                                  bg-clip-text text-transparent group-hover:scale-105 
                                  transition-transform duration-300`}>
                      {step.title}
                    </h3>
                    <p className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300">
                      {step.description}
                    </p>
                  </div>

                  {/* Connecting Line (except for last item) */}
                  {index < STEPS.length - 1 && (
                    <motion.div
                      className="absolute -bottom-8 left-1/2 w-0.5 h-8 bg-gradient-to-b from-primary-500/50 to-transparent"
                      initial={{ height: 0 }}
                      whileInView={{ height: 32 }}
                      viewport={{ once: true }}
                    />
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <motion.div
        className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r 
                   from-transparent via-primary-500/50 to-transparent"
        animate={{
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
    </section>
  );
}